import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiSave, FiPlus, FiTrash2, FiStar } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { TestimonialsContent, Testimonial } from '../../../types/content'
import toast from 'react-hot-toast'

const TestimonialsEditor: React.FC = () => {
  const { content, loading, saving, saveContent } = useContent('testimonials')
  const [formData, setFormData] = useState<TestimonialsContent>({
    title: '',
    subtitle: '',
    testimonials: []
  })
  const [validationErrors, setValidationErrors] = useState<{[key: string]: boolean}>({})

  useEffect(() => {
    if (content?.data) {
      const testimonialsData = content.data as TestimonialsContent
      setFormData(testimonialsData)
    }
  }, [content])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    setValidationErrors({})
    
    const errors: {[key: string]: boolean} = {}
    let hasErrors = false
    
    formData.testimonials.forEach((testimonial, index) => {
      if (!testimonial.reviewerName.trim()) {
        errors[`reviewerName-${index}`] = true
        hasErrors = true
      }
      if (!testimonial.reviewText.trim()) {
        errors[`reviewText-${index}`] = true
        hasErrors = true
      }
      if (testimonial.rating < 1 || testimonial.rating > 5) {
        errors[`rating-${index}`] = true
        hasErrors = true
      }
    })
    
    if (hasErrors) {
      setValidationErrors(errors)
      toast.error('Please fill in all required fields')
      return
    }
    
    try {
      const success = await saveContent(formData)
      
      if (success) {
        toast.success('Testimonials updated successfully!')
      } else {
        toast.error('Failed to save testimonials')
      }
    } catch (error) {
      console.error('Error during save:', error)
      toast.error('An error occurred while saving')
    }
  }

  const addTestimonial = () => {
    const newTestimonial: Testimonial = {
      id: `testimonial-${Date.now()}`,
      reviewerName: 'Customer Name',
      rating: 5,
      reviewText: 'Enter review text here...',
      date: '',
      order: formData.testimonials.length + 1
    }
    setFormData({
      ...formData,
      testimonials: [...formData.testimonials, newTestimonial]
    })
  }

  const updateTestimonial = (index: number, field: keyof Testimonial, value: string | number) => {
    const updatedTestimonials = [...formData.testimonials]
    updatedTestimonials[index] = {
      ...updatedTestimonials[index],
      [field]: value
    }
    setFormData({
      ...formData,
      testimonials: updatedTestimonials
    })
  }

  const removeTestimonial = (index: number) => {
    const updatedTestimonials = formData.testimonials.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      testimonials: updatedTestimonials
    })
  }

  const moveTestimonial = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= formData.testimonials.length) return

    const updatedTestimonials = [...formData.testimonials]
    const temp = updatedTestimonials[index]
    updatedTestimonials[index] = updatedTestimonials[newIndex]
    updatedTestimonials[newIndex] = temp

    updatedTestimonials.forEach((testimonial, i) => {
      testimonial.order = i + 1
    })

    setFormData({
      ...formData,
      testimonials: updatedTestimonials
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-neutral-600">Loading testimonials...</span>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Section Header */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <h3 className="text-lg font-semibold text-neutral-800 mb-6">Section Settings</h3>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="testimonials-section-title" className="block text-sm font-medium text-neutral-700 mb-2">
                Section Title
              </label>
              <input
                id="testimonials-section-title"
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                placeholder="What Our Clients Say"
              />
            </div>

            <div>
              <label htmlFor="testimonials-section-subtitle" className="block text-sm font-medium text-neutral-700 mb-2">
                Subtitle (Optional)
              </label>
              <input
                id="testimonials-section-subtitle"
                type="text"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                placeholder="Hear from our satisfied clients"
              />
            </div>
          </div>
        </div>

        {/* Testimonials List */}
        <div className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center space-x-2">
              <FiStar size={18} />
              <span>Testimonials</span>
            </h3>
            
            <button
              type="button"
              onClick={addTestimonial}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200"
            >
              <FiPlus size={16} />
              <span>Add Testimonial</span>
            </button>
          </div>

          <div className="space-y-4">
            {formData.testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className="p-4 border border-neutral-200 rounded-xl space-y-4"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-neutral-700">
                    Testimonial {index + 1}
                  </span>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => moveTestimonial(index, 'up')}
                      disabled={index === 0}
                      className="p-2 text-neutral-600 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Move Up"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveTestimonial(index, 'down')}
                      disabled={index === formData.testimonials.length - 1}
                      className="p-2 text-neutral-600 hover:text-primary disabled:opacity-30 disabled:cursor-not-allowed transition-colors duration-200"
                      title="Move Down"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeTestimonial(index)}
                      className="text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`reviewerName-${index}`} className="block text-sm font-medium text-neutral-700 mb-2">
                      Reviewer Name *
                    </label>
                    <input
                      id={`reviewerName-${index}`}
                      value={testimonial.reviewerName}
                      onChange={(e) => updateTestimonial(index, 'reviewerName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
                        validationErrors[`reviewerName-${index}`] ? 'border-red-500' : 'border-neutral-300'
                      }`}
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label htmlFor={`rating-${index}`} className="block text-sm font-medium text-neutral-700 mb-2">
                      Rating *
                    </label>
                    <select
                      id={`rating-${index}`}
                      value={testimonial.rating}
                      onChange={(e) => updateTestimonial(index, 'rating', Number.parseInt(e.target.value, 10))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 ${
                        validationErrors[`rating-${index}`] ? 'border-red-500' : 'border-neutral-300'
                      }`}
                    >
                      <option value={5}>5 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={2}>2 Stars</option>
                      <option value={1}>1 Star</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label htmlFor={`reviewText-${index}`} className="block text-sm font-medium text-neutral-700 mb-2">
                      Review Text *
                    </label>
                    <textarea
                      id={`reviewText-${index}`}
                      value={testimonial.reviewText}
                      onChange={(e) => updateTestimonial(index, 'reviewText', e.target.value)}
                      rows={4}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 resize-vertical ${
                        validationErrors[`reviewText-${index}`] ? 'border-red-500' : 'border-neutral-300'
                      }`}
                      placeholder="Enter the review text..."
                    />
                  </div>

                  <div>
                    <label htmlFor={`date-${index}`} className="block text-sm font-medium text-neutral-700 mb-2">
                      Date (Optional)
                    </label>
                    <input
                      id={`date-${index}`}
                      value={testimonial.date || ''}
                      onChange={(e) => updateTestimonial(index, 'date', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                      placeholder="January 2024"
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.testimonials.length === 0 && (
              <div className="text-center py-12 text-neutral-500">
                No testimonials yet. Click "Add Testimonial" to get started.
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-xl hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg"
          >
            <FiSave size={18} />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </motion.button>
        </div>
      </form>
    </div>
  )
}

export default TestimonialsEditor
